import {
  collection,
  doc,
  getDocs,
  getDoc,
  addDoc,
  updateDoc,
  setDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  limit,
  onSnapshot,
  Timestamp,
  increment,
  deleteField,
  writeBatch,
  type QueryDocumentSnapshot,
  type QuerySnapshot,
} from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";
import { auth, db, storage } from "./config";
import { Product, ProductMarketFitQA, Column, Board, BoardInvitation, BoardShareLink, Comment, Activity, FileAttachment, Resource, ResourceAllocation, ResourceRole, DEFAULT_RESOURCE_ROLE_NAMES } from "../types";
import { ref, uploadBytes, getDownloadURL, deleteObject } from "firebase/storage";

// Set to true after the first successful auth warm-up so subsequent calls
// skip the one-time macro-task yield that lets the Firestore SDK process its
// own onIdTokenChanged update before the first read fires.
let _authTokenWarmedUp = false;

/**
 * Waits for Firebase Auth to fully restore the session and ensures the
 * Firestore SDK has had time to propagate the identity token.
 *
 * Why the setTimeout(0): auth.authStateReady() resolves once the Auth SDK
 * has re-hydrated from IndexedDB, but the Firestore SDK processes its own
 * token via an async onIdTokenChanged handler. The macro-task yield lets
 * that processing complete before the first Firestore read fires, closing
 * the window where the SDK sends an unauthenticated request.
 */
async function ensureAuthReady(): Promise<void> {
  await auth.authStateReady();
  const u = auth.currentUser;
  if (u) {
    await u.getIdToken();
    if (!_authTokenWarmedUp) {
      _authTokenWarmedUp = true;
      await new Promise<void>(resolve => setTimeout(resolve, 0));
    }
  }
}

/** Rebuild `memberUserIds` / `memberRoles` from `members` (server verifies membership). */
export async function syncBoardMemberIndexViaCallable(boardId: string): Promise<void> {
  const fn = httpsCallable(getFunctions(), "syncBoardMemberIndex");
  await fn({ boardId });
}

function isFirebasePermissionDenied(e: unknown): boolean {
  return (
    typeof e === "object" &&
    e !== null &&
    "code" in e &&
    (e as { code: string }).code === "permission-denied"
  );
}

/**
 * Run a Firestore read; on permission-denied, perform two repairs then retry:
 * 1. Force-refresh the auth token — covers timing races where the Firestore
 *    SDK received a read before the token was fully propagated.
 * 2. Sync the board member index via callable — covers stale denormalization
 *    where memberUserIds was out of date and the rules correctly denied access.
 */
async function runWithMemberIndexRepair<T>(boardId: string, fn: () => Promise<T>): Promise<T> {
  try {
    return await fn();
  } catch (e) {
    if (!isFirebasePermissionDenied(e)) throw e;
    console.warn('[runWithMemberIndexRepair] permission-denied on boardId:', boardId, 'uid:', auth.currentUser?.uid, '— forcing token refresh + sync');
    // Force a fresh token so the retry request carries valid credentials.
    await auth.currentUser?.getIdToken(true).catch((err) =>
      console.warn('[runWithMemberIndexRepair] token refresh failed:', err)
    );
    await syncBoardMemberIndexViaCallable(boardId).catch((err) =>
      console.warn('[runWithMemberIndexRepair] sync failed:', err)
    );
    return await fn();
  }
}

// Convert Firestore timestamp to Date
const convertTimestamp = (timestamp: Timestamp): Date => {
  return timestamp.toDate();
};

// Convert Date to Firestore timestamp
const convertToTimestamp = (date: Date): Timestamp => {
  return Timestamp.fromDate(date);
};

// Convert optional Firestore timestamp to Date (for optional date fields)
const convertOptionalTimestamp = (value: unknown): Date | undefined => {
  if (value == null) return undefined;
  if (value instanceof Date) return value;
  if (typeof (value as Timestamp).toDate === 'function') return (value as Timestamp).toDate();
  return undefined;
};

const parseMarketFitQa = (raw: unknown): ProductMarketFitQA[] | undefined => {
  if (!Array.isArray(raw) || raw.length === 0) return undefined;
  const parsed: ProductMarketFitQA[] = [];
  for (const item of raw) {
    if (!item || typeof item !== 'object') continue;
    const o = item as Record<string, unknown>;
    const id = o.id != null ? String(o.id) : '';
    if (!id) continue;
    parsed.push({
      id,
      question: o.question != null ? String(o.question) : '',
      answer: o.answer != null && String(o.answer).trim() !== '' ? String(o.answer) : undefined,
      createdAt: convertOptionalTimestamp(o.createdAt) ?? new Date(0),
      updatedAt: convertOptionalTimestamp(o.updatedAt) ?? new Date(0),
      createdBy: o.createdBy != null ? String(o.createdBy) : '',
      createdByEmail: o.createdByEmail != null ? String(o.createdByEmail) : undefined,
    });
  }
  return parsed.length > 0 ? parsed : undefined;
};

const mapProductDoc = (id: string, data: Record<string, unknown>): Product => ({
  id,
  ...data,
  createdAt: convertTimestamp(data.createdAt as Timestamp),
  updatedAt: convertTimestamp(data.updatedAt as Timestamp),
  startDate: convertOptionalTimestamp(data.startDate),
  endDate: convertOptionalTimestamp(data.endDate),
  marketFitQa: parseMarketFitQa(data.marketFitQa),
} as Product);

// Remove undefined values from objects (Firestore doesn't accept undefined)
// Recursively cleans nested objects and arrays of objects so no undefined is passed to addDoc
const removeUndefinedValues = (obj: any): any => {
  if (obj === null || obj === undefined) {
    return obj;
  }
  // Don't recurse into Date or Firestore Timestamp
  if (obj instanceof Date || (typeof obj === 'object' && typeof (obj as Timestamp).toDate === 'function')) {
    return obj;
  }
  if (Array.isArray(obj)) {
    return obj.map((item) => removeUndefinedValues(item));
  }
  if (typeof obj === 'object' && (obj.constructor === Object || Object.prototype.toString.call(obj) === '[object Object]')) {
    const cleaned: any = {};
    for (const [key, value] of Object.entries(obj)) {
      if (value !== undefined) {
        cleaned[key] = removeUndefinedValues(value);
      }
    }
    return cleaned;
  }
  return obj;
};

/** Keeps `memberUserIds` / `memberRoles` aligned with `members` for `firestore.rules`. */
export const buildMemberIndexFromMembers = (
  members: Array<{ userId?: string; uid?: string; role?: string }>
): { memberUserIds: string[]; memberRoles: Record<string, string> } => {
  const memberUserIds: string[] = [];
  const memberRoles: Record<string, string> = {};
  for (const m of members) {
    const uid = m.userId || m.uid;
    if (!uid) continue;
    memberUserIds.push(uid);
    if (m.role) memberRoles[uid] = m.role;
  }
  return { memberUserIds, memberRoles };
};

const boardFromSnapshot = (docSnap: QueryDocumentSnapshot): Board => {
  const data = docSnap.data();
  return {
    id: docSnap.id,
    ...data,
    createdAt: convertTimestamp(data.createdAt as Timestamp),
    updatedAt: convertTimestamp(data.updatedAt as Timestamp),
    members: (data.members || []).map((member: Record<string, unknown> & { joinedAt?: Timestamp }) => ({
      ...member,
      joinedAt: convertTimestamp(member.joinedAt as Timestamp),
    })),
  } as Board;
};

/** Owner-only repair for boards created before member index fields existed. */
const repairBoardMemberIndexIfNeeded = async (
  board: Board,
  viewerUserId: string
): Promise<void> => {
  if (board.ownerId !== viewerUserId || !board.members?.length) return;
  const { memberUserIds, memberRoles } = buildMemberIndexFromMembers(board.members);
  const boardRef = doc(db, BOARDS_COLLECTION, board.id);
  const snap = await getDoc(boardRef);
  if (!snap.exists()) return;
  const raw = snap.data();
  const existing = raw.memberUserIds as string[] | undefined;
  const same =
    existing &&
    existing.length === memberUserIds.length &&
    [...existing].sort().join(',') === [...memberUserIds].sort().join(',');
  if (same && raw.memberRoles) return;
  await updateDoc(boardRef, {
    memberUserIds,
    memberRoles,
    updatedAt: convertToTimestamp(new Date()),
  });
};

// Collections
const PRODUCTS_COLLECTION = "products";
const COLUMNS_COLLECTION = "columns";
const BOARDS_COLLECTION = "boards";
const INVITATIONS_COLLECTION = "invitations";
const SHARE_LINKS_COLLECTION = "shareLinks";
const COMMENTS_COLLECTION = "comments";
const ACTIVITIES_COLLECTION = "activities";
const PRESENCE_COLLECTION = "presence";
const RESOURCES_COLLECTION = "resources";
const RESOURCE_ALLOCATIONS_COLLECTION = "resourceAllocations";
const RESOURCE_ROLES_COLLECTION = "resourceRoles";
const STANDUP_ENTRIES_COLLECTION = "standupEntries";

// Product Services
export const getProducts = async (boardId: string): Promise<Product[]> => {
  try {
    await ensureAuthReady();
    const q = query(
      collection(db, PRODUCTS_COLLECTION),
      where("boardId", "==", boardId),
      orderBy("createdAt", "desc")
    );
    const querySnapshot = await runWithMemberIndexRepair(boardId, () => getDocs(q));

    return querySnapshot.docs.map(doc => mapProductDoc(doc.id, doc.data() as Record<string, unknown>));
  } catch (error) {
    console.error("Error getting products:", error);
    throw new Error("Failed to fetch products");
  }
};

export const getProduct = async (productId: string): Promise<Product | null> => {
  try {
    const docRef = doc(db, PRODUCTS_COLLECTION, productId);
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      return mapProductDoc(docSnap.id, docSnap.data() as Record<string, unknown>);
    }
    return null;
  } catch (error) {
    console.error("Error getting product:", error);
    throw new Error("Failed to fetch product");
  }
};

export const addProduct = async (product: Omit<Product, 'id' | 'createdAt' | 'updatedAt'>, userId: string): Promise<string> => {
  try {
    const productData = removeUndefinedValues({
      ...product,
      createdBy: userId,
      commentCount: 0,
      createdAt: convertToTimestamp(new Date()),
      updatedAt: convertToTimestamp(new Date()),
      startDate: product.startDate ? convertToTimestamp(product.startDate) : undefined,
      endDate: product.endDate ? convertToTimestamp(product.endDate) : undefined,
    });
    
    const docRef = await addDoc(collection(db, PRODUCTS_COLLECTION), productData);
    return docRef.id;
  } catch (error) {
    console.error("Error adding product:", error);
    throw new Error("Failed to add product");
  }
};

export const updateProduct = async (productId: string, updates: Partial<Product>): Promise<void> => {
  try {
    const docRef = doc(db, PRODUCTS_COLLECTION, productId);
    const updateData: Record<string, unknown> = {
      ...updates,
      updatedAt: convertToTimestamp(new Date()),
    };
    if (updates.startDate !== undefined) updateData.startDate = updates.startDate ? convertToTimestamp(updates.startDate) : deleteField();
    if (updates.endDate !== undefined) updateData.endDate = updates.endDate ? convertToTimestamp(updates.endDate) : deleteField();
    
    await updateDoc(docRef, removeUndefinedValues(updateData));
  } catch (error) {
    console.error("Error updating product:", error);
    throw new Error("Failed to update product");
  }
};

export const deleteProduct = async (productId: string): Promise<void> => {
  try {
    const docRef = doc(db, PRODUCTS_COLLECTION, productId);
    await deleteDoc(docRef);
  } catch (error) {
    console.error("Error deleting product:", error);
    throw new Error("Failed to delete product");
  }
};

// Column Services
export const getColumns = async (boardId: string): Promise<Column[]> => {
  try {
    await ensureAuthReady();
    const q = query(
      collection(db, COLUMNS_COLLECTION),
      where("boardId", "==", boardId),
      orderBy("order", "asc")
    );
    const querySnapshot = await runWithMemberIndexRepair(boardId, () => getDocs(q));

    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
    })) as Column[];
  } catch (error) {
    console.error("Error getting columns:", error);
    throw new Error("Failed to fetch columns");
  }
};

export const addColumn = async (column: Omit<Column, 'id'>, boardId: string): Promise<string> => {
  try {
    const columnData = {
      ...column,
      boardId,
    };
    
    const docRef = await addDoc(collection(db, COLUMNS_COLLECTION), columnData);
    return docRef.id;
  } catch (error) {
    console.error("Error adding column:", error);
    throw new Error("Failed to add column");
  }
};

/** Get the first column id for a resource manager board, or create the default "Projects" column if none exists. */
export const ensureResourceManagerProjectsColumn = async (boardId: string): Promise<string | null> => {
  try {
    const cols = await getColumns(boardId);
    if (cols.length > 0) return cols[0].id;
    const id = await addColumn(
      { title: 'Projects', color: '#4d9a4d', order: 1, boardId },
      boardId
    );
    return id;
  } catch (error) {
    console.error("Error ensuring projects column:", error);
    return null;
  }
};

export const updateColumn = async (columnId: string, updates: Partial<Column>): Promise<void> => {
  try {
    const docRef = doc(db, COLUMNS_COLLECTION, columnId);
    await updateDoc(docRef, updates);
  } catch (error) {
    console.error("Error updating column:", error);
    throw new Error("Failed to update column");
  }
};

export const deleteColumn = async (columnId: string): Promise<void> => {
  try {
    const docRef = doc(db, COLUMNS_COLLECTION, columnId);
    await deleteDoc(docRef);
  } catch (error) {
    console.error("Error deleting column:", error);
    throw new Error("Failed to delete column");
  }
};

// Real-time listeners
export const subscribeToProducts = (boardId: string, callback: (products: Product[]) => void) => {
  let unsub: (() => void) | undefined;
  let cancelled = false;
  void ensureAuthReady().then(async () => {
    if (cancelled) return;
    console.debug('[subscribeToProducts] boardId:', boardId, 'uid:', auth.currentUser?.uid);
    await syncBoardMemberIndexViaCallable(boardId).catch((e) =>
      console.warn('[subscribeToProducts] sync failed:', e)
    );
    const q = query(
      collection(db, PRODUCTS_COLLECTION),
      where("boardId", "==", boardId),
      orderBy("createdAt", "desc")
    );
    unsub = onSnapshot(
      q,
      (querySnapshot) => {
        const products = querySnapshot.docs.map(doc =>
          mapProductDoc(doc.id, doc.data() as Record<string, unknown>)
        );
        callback(products);
      },
      (err) => console.error("subscribeToProducts:", err)
    );
  });
  return () => {
    cancelled = true;
    unsub?.();
  };
};

export const subscribeToColumns = (boardId: string, callback: (columns: Column[]) => void) => {
  let unsub: (() => void) | undefined;
  let cancelled = false;
  void ensureAuthReady().then(async () => {
    if (cancelled) return;
    console.debug('[subscribeToColumns] boardId:', boardId, 'uid:', auth.currentUser?.uid);
    await syncBoardMemberIndexViaCallable(boardId).catch((e) =>
      console.warn('[subscribeToColumns] sync failed:', e)
    );
    const q = query(
      collection(db, COLUMNS_COLLECTION),
      where("boardId", "==", boardId),
      orderBy("order", "asc")
    );
    unsub = onSnapshot(
      q,
      (querySnapshot) => {
        const columns = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
        })) as Column[];
        callback(columns);
      },
      (err) => console.error("subscribeToColumns:", err)
    );
  });
  return () => {
    cancelled = true;
    unsub?.();
  };
};

// Board Services
export const getBoardsForUser = async (userId: string): Promise<Board[]> => {
  try {
    await ensureAuthReady();
    const ownerQuery = query(
      collection(db, BOARDS_COLLECTION),
      where('ownerId', '==', userId),
      orderBy('updatedAt', 'desc')
    );
    const memberQuery = query(
      collection(db, BOARDS_COLLECTION),
      where('memberUserIds', 'array-contains', userId),
      orderBy('updatedAt', 'desc')
    );

    // Run separately: if one query fails (e.g. index still building), still show boards from the other.
    let ownerSnap: QuerySnapshot | null = null;
    let memberSnap: QuerySnapshot | null = null;
    try {
      ownerSnap = await getDocs(ownerQuery);
    } catch (e) {
      console.error('getBoardsForUser: ownerId query failed:', e);
    }
    try {
      memberSnap = await getDocs(memberQuery);
    } catch (e) {
      console.error('getBoardsForUser: memberUserIds query failed:', e);
    }

    const byId = new Map<string, Board>();
    ownerSnap?.docs.forEach((d) => byId.set(d.id, boardFromSnapshot(d)));
    memberSnap?.docs.forEach((d) => byId.set(d.id, boardFromSnapshot(d)));

    const userBoards = Array.from(byId.values()).sort(
      (a, b) => b.updatedAt.getTime() - a.updatedAt.getTime()
    );

    await Promise.all(userBoards.map((b) => repairBoardMemberIndexIfNeeded(b, userId)));

    return userBoards;
  } catch (error) {
    console.error('Error getting boards:', error);
    throw new Error('Failed to fetch boards');
  }
};

export const createBoard = async (board: Omit<Board, 'id' | 'createdAt' | 'updatedAt'>, userId: string): Promise<string> => {
  try {
    const membersStored = board.members.map((member: { userId: string; email: string; name?: string; role: string; joinedAt: Date }) => ({
      ...member,
      joinedAt: convertToTimestamp(member.joinedAt),
    }));
    const memberIndex = buildMemberIndexFromMembers(board.members);
    const boardData = removeUndefinedValues({
      ...board,
      members: membersStored,
      memberUserIds: memberIndex.memberUserIds,
      memberRoles: memberIndex.memberRoles,
      createdAt: convertToTimestamp(new Date()),
      updatedAt: convertToTimestamp(new Date()),
    });
    
    const docRef = await addDoc(collection(db, BOARDS_COLLECTION), boardData);
    const boardId = docRef.id;
    
    // Create default columns based on board type
    if (board.boardType === 'opportunity') {
      await createDefaultOpportunityColumns(boardId);
    } else if (board.boardType === 'product') {
      await createDefaultProductColumns(boardId);
    } else if (board.boardType === 'resource_manager') {
      await createDefaultResourceManagerColumns(boardId);
    }
    
    return boardId;
  } catch (error) {
    console.error('Error creating board:', error);
    throw new Error('Failed to create board');
  }
};

// Helper function to create default opportunity columns
const createDefaultOpportunityColumns = async (boardId: string): Promise<void> => {
  const defaultColumns = [
    { title: 'Prospecting', color: '#8a6be8', order: 1 },
    { title: 'Qualified', color: '#e86a94', order: 2 },
    { title: 'Proposal', color: '#4d9a4d', order: 3 },
    { title: 'Negotiation', color: '#f2752a', order: 4 },
    { title: 'Closed Won', color: '#6b6b6b', order: 5 },
    { title: 'Closed Lost', color: '#ef4444', order: 6 },
  ];

  try {
    // Create all columns in parallel
    const columnPromises = defaultColumns.map(column => 
      addColumn({
        title: column.title,
        color: column.color,
        order: column.order,
        boardId,
      }, boardId)
    );
    
    await Promise.all(columnPromises);
    console.log('Default opportunity columns created successfully');
  } catch (error) {
    console.error('Error creating default opportunity columns:', error);
    // Don't throw here - board creation should still succeed even if default columns fail
  }
};

// Helper function to create default product columns
const createDefaultProductColumns = async (boardId: string): Promise<void> => {
  const defaultColumns = [
    { title: 'Ideas', color: '#8a6be8', order: 1 },
    { title: 'Research', color: '#e86a94', order: 2 },
    { title: 'Development', color: '#4d9a4d', order: 3 },
    { title: 'Testing', color: '#f2752a', order: 4 },
    { title: 'Launch', color: '#6b6b6b', order: 5 },
  ];

  try {
    // Create all columns in parallel
    const columnPromises = defaultColumns.map(column => 
      addColumn({
        title: column.title,
        color: column.color,
        order: column.order,
        boardId,
      }, boardId)
    );
    
    await Promise.all(columnPromises);
    console.log('Default product columns created successfully');
  } catch (error) {
    console.error('Error creating default product columns:', error);
    // Don't throw here - board creation should still succeed even if default columns fail
  }
};

// Helper function to create default resource manager columns (single "Projects" column)
const createDefaultResourceManagerColumns = async (boardId: string): Promise<void> => {
  try {
    await addColumn({
      title: 'Projects',
      color: '#4d9a4d',
      order: 1,
      boardId,
    }, boardId);
    console.log('Default resource manager column created successfully');
  } catch (error) {
    console.error('Error creating default resource manager column:', error);
  }
};

export const updateBoard = async (boardId: string, updates: Partial<Board>): Promise<void> => {
  try {
    const docRef = doc(db, BOARDS_COLLECTION, boardId);
    const updateData = {
      ...updates,
      updatedAt: convertToTimestamp(new Date()),
    };
    
    await updateDoc(docRef, updateData);
  } catch (error) {
    console.error('Error updating board:', error);
    throw new Error('Failed to update board');
  }
};

export const deleteBoard = async (boardId: string): Promise<void> => {
  try {
    // Delete all products in the board
    const productsQuery = query(
      collection(db, PRODUCTS_COLLECTION),
      where('boardId', '==', boardId)
    );
    const productsSnapshot = await getDocs(productsQuery);
    const productDeletions = productsSnapshot.docs.map(doc => deleteDoc(doc.ref));
    
    // Delete all columns in the board
    const columnsQuery = query(
      collection(db, COLUMNS_COLLECTION),
      where('boardId', '==', boardId)
    );
    const columnsSnapshot = await getDocs(columnsQuery);
    const columnDeletions = columnsSnapshot.docs.map(doc => deleteDoc(doc.ref));
    
    // Delete all comments for products in the board
    const commentsQuery = query(
      collection(db, COMMENTS_COLLECTION),
      where('boardId', '==', boardId)
    );
    const commentsSnapshot = await getDocs(commentsQuery);
    const commentDeletions = commentsSnapshot.docs.map(doc => deleteDoc(doc.ref));
    
    // Delete all activities for the board
    const activitiesQuery = query(
      collection(db, ACTIVITIES_COLLECTION),
      where('boardId', '==', boardId)
    );
    const activitiesSnapshot = await getDocs(activitiesQuery);
    const activityDeletions = activitiesSnapshot.docs.map(doc => deleteDoc(doc.ref));
    
    // Delete all share links for the board
    const shareLinksQuery = query(
      collection(db, SHARE_LINKS_COLLECTION),
      where('boardId', '==', boardId)
    );
    const shareLinksSnapshot = await getDocs(shareLinksQuery);
    const shareLinkDeletions = shareLinksSnapshot.docs.map(doc => deleteDoc(doc.ref));
    
    // Delete all invitations for the board
    const invitationsQuery = query(
      collection(db, INVITATIONS_COLLECTION),
      where('boardId', '==', boardId)
    );
    const invitationsSnapshot = await getDocs(invitationsQuery);
    const invitationDeletions = invitationsSnapshot.docs.map(doc => deleteDoc(doc.ref));
    
    // Delete the board itself
    const boardRef = doc(db, BOARDS_COLLECTION, boardId);
    const boardDeletion = deleteDoc(boardRef);
    
    // Execute all deletions in parallel
    await Promise.all([
      ...productDeletions,
      ...columnDeletions,
      ...commentDeletions,
      ...activityDeletions,
      ...shareLinkDeletions,
      ...invitationDeletions,
      boardDeletion
    ]);
    
  } catch (error) {
    console.error('Error deleting board:', error);
    throw new Error('Failed to delete board');
  }
};

// Invitation Services
export const createInvitation = async (invitation: Omit<BoardInvitation, 'id' | 'createdAt' | 'expiresAt'>): Promise<string> => {
  try {
    // Check for existing pending invitation to the same email for the same board
    const existingInvitationQuery = query(
      collection(db, INVITATIONS_COLLECTION),
      where('boardId', '==', invitation.boardId),
      where('email', '==', invitation.email),
      where('status', '==', 'pending')
    );
    
    const existingInvitations = await getDocs(existingInvitationQuery);
    
    if (!existingInvitations.empty) {
      throw new Error('An invitation to this email already exists for this board');
    }
    
    const invitationData = {
      ...invitation,
      createdAt: convertToTimestamp(new Date()),
      expiresAt: convertToTimestamp(new Date(Date.now() + 7 * 24 * 60 * 60 * 1000)), // 7 days
    };
    
    const docRef = await addDoc(collection(db, INVITATIONS_COLLECTION), invitationData);
    return docRef.id;
  } catch (error) {
    console.error('Error creating invitation:', error);
    throw new Error('Failed to create invitation');
  }
};

export const getInvitationsForUser = async (email: string): Promise<BoardInvitation[]> => {
  try {
    // Normalize email to lowercase for consistent matching
    const normalizedEmail = email.toLowerCase().trim();
    
    // First, get all invitations for this email
    const q = query(
      collection(db, INVITATIONS_COLLECTION),
      where('email', '==', normalizedEmail),
      where('status', '==', 'pending')
    );
    const querySnapshot = await getDocs(q);
    
    // Filter out expired invitations on the client side
    const now = new Date();
    const validInvitations = querySnapshot.docs
      .map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          createdAt: convertTimestamp(data.createdAt),
          expiresAt: convertTimestamp(data.expiresAt),
        } as BoardInvitation;
      })
      .filter(invitation => invitation.expiresAt > now)
      .sort((a, b) => b.expiresAt.getTime() - a.expiresAt.getTime());
    
    return validInvitations;
  } catch (error) {
    console.error('Error getting invitations:', error);
    throw new Error('Failed to fetch invitations');
  }
};

export const updateInvitationStatus = async (invitationId: string, status: 'accepted' | 'declined'): Promise<void> => {
  try {
    const docRef = doc(db, INVITATIONS_COLLECTION, invitationId);
    await updateDoc(docRef, { status });
  } catch (error) {
    console.error('Error updating invitation:', error);
    throw new Error('Failed to update invitation');
  }
};

// Debug function to check all invitations (commented out to prevent console spam)
// export const debugInvitations = async (): Promise<void> => {
//   try {
//     const querySnapshot = await getDocs(collection(db, INVITATIONS_COLLECTION));
//     console.log('All invitations in database:');
//     querySnapshot.docs.forEach(doc => {
//       console.log('Invitation:', doc.id, doc.data());
//     });
//   } catch (error) {
//     console.error('Error debugging invitations:', error);
//   }
// };

export const getPendingInvitationsForBoard = async (boardId: string): Promise<BoardInvitation[]> => {
  try {
    const q = query(
      collection(db, INVITATIONS_COLLECTION),
      where('boardId', '==', boardId),
      where('status', '==', 'pending')
    );
    const querySnapshot = await getDocs(q);
    
    const now = new Date();
    return querySnapshot.docs
      .map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          createdAt: convertTimestamp(data.createdAt),
          expiresAt: convertTimestamp(data.expiresAt),
        } as BoardInvitation;
      })
      .filter(invitation => invitation.expiresAt > now)
      .sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
  } catch (error) {
    console.error('Error getting pending invitations for board:', error);
    throw new Error('Failed to fetch pending invitations');
  }
};

export const cancelInvitation = async (invitationId: string): Promise<void> => {
  try {
    const docRef = doc(db, INVITATIONS_COLLECTION, invitationId);
    await updateDoc(docRef, { status: 'cancelled' });
  } catch (error) {
    console.error('Error cancelling invitation:', error);
    throw new Error('Failed to cancel invitation');
  }
};

export const addUserToBoard = async (boardId: string, userId: string, email: string, role: 'admin' | 'editor' | 'viewer'): Promise<void> => {
  try {
    const boardRef = doc(db, BOARDS_COLLECTION, boardId);
    const boardDoc = await getDoc(boardRef);
    
    if (!boardDoc.exists()) {
      throw new Error('Board not found');
    }
    
    const boardData = boardDoc.data();
    const members = boardData.members || [];
    
    // Check if user is already a member
    const existingMemberIndex = members.findIndex((member: any) => member.userId === userId);
    
    if (existingMemberIndex >= 0) {
      // User is already a member - preserve their existing role
      const existingMember = members[existingMemberIndex];
      console.log(`User ${email} is already a member with role: ${existingMember.role}. Preserving existing permissions.`);
      return; // Don't overwrite existing role
    } else {
      // Add new member
      members.push({
        userId,
        email: email.toLowerCase().trim(),
        role,
        joinedAt: convertToTimestamp(new Date()),
      });
    }

    const memberIndex = buildMemberIndexFromMembers(members);
    
    await updateDoc(boardRef, { 
      members,
      memberUserIds: memberIndex.memberUserIds,
      memberRoles: memberIndex.memberRoles,
      updatedAt: convertToTimestamp(new Date())
    });
  } catch (error) {
    console.error('Error adding user to board:', error);
    throw new Error('Failed to add user to board');
  }
};

export const updateUserRole = async (boardId: string, userId: string, newRole: 'owner' | 'admin' | 'editor' | 'viewer'): Promise<void> => {
  try {
    const boardRef = doc(db, BOARDS_COLLECTION, boardId);
    const boardDoc = await getDoc(boardRef);
    
    if (!boardDoc.exists()) {
      throw new Error('Board not found');
    }
    
    const boardData = boardDoc.data();
    const members = boardData.members || [];
    
    // Check if user is already a member
    const existingMemberIndex = members.findIndex((member: any) => member.userId === userId);
    
    if (existingMemberIndex >= 0) {
      // Update existing member's role
      members[existingMemberIndex].role = newRole;

      const memberIndex = buildMemberIndexFromMembers(members);
      
      await updateDoc(boardRef, { 
        members,
        memberUserIds: memberIndex.memberUserIds,
        memberRoles: memberIndex.memberRoles,
        updatedAt: convertToTimestamp(new Date())
      });
    } else {
      throw new Error('User is not a member of this board');
    }
  } catch (error) {
    console.error('Error updating user role:', error);
    throw new Error('Failed to update user role');
  }
};

export const removeBoardMember = async (boardId: string, userId: string): Promise<void> => {
  try {
    const boardRef = doc(db, BOARDS_COLLECTION, boardId);
    const boardDoc = await getDoc(boardRef);
    
    if (!boardDoc.exists()) {
      throw new Error('Board not found');
    }
    
    const boardData = boardDoc.data();
    const members = boardData.members || [];
    
    // Check if user is a member
    const existingMemberIndex = members.findIndex((member: any) => member.userId === userId);
    
    if (existingMemberIndex >= 0) {
      // Remove the member
      members.splice(existingMemberIndex, 1);

      const memberIndex = buildMemberIndexFromMembers(members);
      
      await updateDoc(boardRef, { 
        members,
        memberUserIds: memberIndex.memberUserIds,
        memberRoles: memberIndex.memberRoles,
        updatedAt: convertToTimestamp(new Date())
      });
    } else {
      throw new Error('User is not a member of this board');
    }
  } catch (error) {
    console.error('Error removing board member:', error);
    throw new Error('Failed to remove board member');
  }
};

// Real-time listeners for boards
export const subscribeToBoards = (userId: string, callback: (boards: Board[]) => void) => {
  let unsubOwner: (() => void) | undefined;
  let unsubMember: (() => void) | undefined;
  let cancelled = false;

  let ownerBoards = new Map<string, Board>();
  let memberBoards = new Map<string, Board>();

  const mergeAndEmit = () => {
    const byId = new Map<string, Board>();
    ownerBoards.forEach((b, id) => byId.set(id, b));
    memberBoards.forEach((b, id) => byId.set(id, b));
    const boards = Array.from(byId.values()).sort(
      (a, b) => b.updatedAt.getTime() - a.updatedAt.getTime()
    );
    callback(boards);
    void Promise.all(boards.map((b) => repairBoardMemberIndexIfNeeded(b, userId)));
  };

  const logSnapError =
    (label: string) =>
    (err: unknown) => {
      console.error(`subscribeToBoards (${label}):`, err);
    };

  void ensureAuthReady().then(() => {
    if (cancelled) return;
    const ownerQuery = query(
      collection(db, BOARDS_COLLECTION),
      where('ownerId', '==', userId),
      orderBy('updatedAt', 'desc')
    );
    const memberQuery = query(
      collection(db, BOARDS_COLLECTION),
      where('memberUserIds', 'array-contains', userId),
      orderBy('updatedAt', 'desc')
    );
    unsubOwner = onSnapshot(
      ownerQuery,
      (snap) => {
        ownerBoards = new Map(snap.docs.map((d) => [d.id, boardFromSnapshot(d)]));
        mergeAndEmit();
      },
      logSnapError('ownerId query')
    );
    unsubMember = onSnapshot(
      memberQuery,
      (snap) => {
        memberBoards = new Map(snap.docs.map((d) => [d.id, boardFromSnapshot(d)]));
        mergeAndEmit();
      },
      logSnapError('memberUserIds query')
    );
  });

  return () => {
    cancelled = true;
    unsubOwner?.();
    unsubMember?.();
  };
};

// Share Link Services
export const createShareLink = async (boardId: string, boardName: string, createdBy: string, createdByEmail: string, role: 'admin' | 'editor' | 'viewer'): Promise<string> => {
  try {
    const shareLinkData = {
      boardId,
      boardName,
      createdBy,
      createdByEmail: createdByEmail.toLowerCase().trim(),
      role,
      isActive: true,
      expiresAt: convertToTimestamp(new Date(Date.now() + 30 * 24 * 60 * 60 * 1000)), // 30 days
      createdAt: convertToTimestamp(new Date()),
      accessCount: 0,
    };
    
    const docRef = await addDoc(collection(db, SHARE_LINKS_COLLECTION), shareLinkData);
    return docRef.id;
  } catch (error) {
    console.error('Error creating share link:', error);
    throw new Error('Failed to create share link');
  }
};

export const getShareLink = async (shareLinkId: string): Promise<BoardShareLink | null> => {
  try {
    const docRef = doc(db, SHARE_LINKS_COLLECTION, shareLinkId);
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      const data = docSnap.data();
      return {
        id: docSnap.id,
        ...data,
        createdAt: convertTimestamp(data.createdAt),
        expiresAt: data.expiresAt ? convertTimestamp(data.expiresAt) : undefined,
      } as BoardShareLink;
    }
    return null;
  } catch (error) {
    console.error('Error getting share link:', error);
    throw new Error('Failed to fetch share link');
  }
};

export const updateShareLinkAccess = async (shareLinkId: string): Promise<void> => {
  try {
    const docRef = doc(db, SHARE_LINKS_COLLECTION, shareLinkId);
    await updateDoc(docRef, { 
      accessCount: increment(1),
      lastAccessed: convertToTimestamp(new Date())
    });
  } catch (error) {
    console.error('Error updating share link access:', error);
    throw new Error('Failed to update share link access');
  }
};

export const deactivateShareLink = async (shareLinkId: string): Promise<void> => {
  try {
    const docRef = doc(db, SHARE_LINKS_COLLECTION, shareLinkId);
    await updateDoc(docRef, { isActive: false });
  } catch (error) {
    console.error('Error deactivating share link:', error);
    throw new Error('Failed to deactivate share link');
  }
};

export const getShareLinksForBoard = async (boardId: string): Promise<BoardShareLink[]> => {
  try {
    const q = query(
      collection(db, SHARE_LINKS_COLLECTION),
      where('boardId', '==', boardId),
      where('isActive', '==', true)
    );
    const querySnapshot = await getDocs(q);
    
    return querySnapshot.docs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        ...data,
        createdAt: convertTimestamp(data.createdAt),
        expiresAt: data.expiresAt ? convertTimestamp(data.expiresAt) : undefined,
      } as BoardShareLink;
    });
  } catch (error) {
    console.error('Error getting share links for board:', error);
    throw new Error('Failed to fetch share links');
  }
};

// Comment Services
export const addComment = async (comment: Omit<Comment, 'id' | 'createdAt' | 'updatedAt'>): Promise<string> => {
  try {
    const commentData = removeUndefinedValues({
      ...comment,
      createdAt: convertToTimestamp(new Date()),
      updatedAt: convertToTimestamp(new Date()),
    });
    
    const docRef = await addDoc(collection(db, COMMENTS_COLLECTION), commentData);
    
    // Update product comment count
    const productRef = doc(db, PRODUCTS_COLLECTION, comment.productId);
    await updateDoc(productRef, {
      commentCount: increment(1)
    });
    
    return docRef.id;
  } catch (error) {
    console.error('Error adding comment:', error);
    throw new Error('Failed to add comment');
  }
};

export const getCommentsForProduct = async (productId: string): Promise<Comment[]> => {
  try {
    const q = query(
      collection(db, COMMENTS_COLLECTION),
      where('productId', '==', productId),
      orderBy('createdAt', 'asc')
    );
    const querySnapshot = await getDocs(q);
    
    return querySnapshot.docs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        ...data,
        createdAt: convertTimestamp(data.createdAt),
        updatedAt: convertTimestamp(data.updatedAt),
      } as Comment;
    });
  } catch (error) {
    console.error('Error getting comments:', error);
    throw new Error('Failed to fetch comments');
  }
};

export const updateComment = async (commentId: string, updates: Partial<Comment>): Promise<void> => {
  try {
    const docRef = doc(db, COMMENTS_COLLECTION, commentId);
    const updateData = removeUndefinedValues({
      ...updates,
      updatedAt: convertToTimestamp(new Date()),
    });
    
    await updateDoc(docRef, updateData);
  } catch (error) {
    console.error('Error updating comment:', error);
    throw new Error('Failed to update comment');
  }
};

export const deleteComment = async (commentId: string): Promise<void> => {
  try {
    // First get the comment to know which product it belongs to
    const commentRef = doc(db, COMMENTS_COLLECTION, commentId);
    const commentSnap = await getDoc(commentRef);
    
    if (commentSnap.exists()) {
      const commentData = commentSnap.data();
      const productId = commentData.productId;
      
      // Delete the comment
      await deleteDoc(commentRef);
      
      // Update product comment count
      const productRef = doc(db, PRODUCTS_COLLECTION, productId);
      await updateDoc(productRef, {
        commentCount: increment(-1)
      });
    }
  } catch (error) {
    console.error('Error deleting comment:', error);
    throw new Error('Failed to delete comment');
  }
};

// Activity Services
export const addActivity = async (activity: Omit<Activity, 'id' | 'createdAt'>): Promise<string> => {
  try {
    const activityData = removeUndefinedValues({
      ...activity,
      createdAt: convertToTimestamp(new Date()),
    });
    
    const docRef = await addDoc(collection(db, ACTIVITIES_COLLECTION), activityData);
    return docRef.id;
  } catch (error) {
    console.error('Error adding activity:', error);
    throw new Error('Failed to add activity');
  }
};

export const getActivitiesForBoard = async (boardId: string, limitCount: number = 50): Promise<Activity[]> => {
  try {
    const q = query(
      collection(db, ACTIVITIES_COLLECTION),
      where('boardId', '==', boardId),
      orderBy('createdAt', 'desc'),
      limit(limitCount)
    );
    const querySnapshot = await getDocs(q);
    
    return querySnapshot.docs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        ...data,
        createdAt: convertTimestamp(data.createdAt),
      } as Activity;
    });
  } catch (error) {
    console.error('Error getting activities:', error);
    throw new Error('Failed to fetch activities');
  }
};

export const getActivitiesForProduct = async (productId: string, limitCount: number = 20): Promise<Activity[]> => {
  try {
    const q = query(
      collection(db, ACTIVITIES_COLLECTION),
      where('productId', '==', productId),
      orderBy('createdAt', 'desc'),
      limit(limitCount)
    );
    const querySnapshot = await getDocs(q);
    
    return querySnapshot.docs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        ...data,
        createdAt: convertTimestamp(data.createdAt),
      } as Activity;
    });
  } catch (error) {
    console.error('Error getting product activities:', error);
    throw new Error('Failed to fetch product activities');
  }
};

// Presence Services
/** Prime member index once per board/tab so presence writes win races with first listeners. */
const presenceMemberIndexPrimed = new Set<string>();

export const setUserPresence = async (boardId: string, userId: string, userEmail: string, userName: string): Promise<void> => {
  try {
    await ensureAuthReady();
    if (!presenceMemberIndexPrimed.has(boardId)) {
      presenceMemberIndexPrimed.add(boardId);
      await syncBoardMemberIndexViaCallable(boardId).catch(() => {
        presenceMemberIndexPrimed.delete(boardId);
      });
    }
    const presenceRef = doc(db, PRESENCE_COLLECTION, `${boardId}_${userId}`);
    const presenceData = {
      boardId,
      userId,
      userEmail,
      userName,
      lastSeen: convertToTimestamp(new Date()),
      isOnline: true,
    };
    console.log('Setting user presence:', presenceData);
    await setDoc(presenceRef, presenceData, { merge: true });
  } catch (error) {
    console.error('Error setting user presence:', error);
  }
};

export const removeUserPresence = async (boardId: string, userId: string): Promise<void> => {
  try {
    await ensureAuthReady();
    const presenceRef = doc(db, PRESENCE_COLLECTION, `${boardId}_${userId}`);
    // Include boardId/userId so merge-create still satisfies presence rules (partial writes omit fields).
    await setDoc(presenceRef, {
      boardId,
      userId,
      isOnline: false,
      lastSeen: convertToTimestamp(new Date()),
    }, { merge: true });
  } catch (error) {
    console.error('Error removing user presence:', error);
  }
};

export const subscribeToBoardPresence = (boardId: string, callback: (activeUsers: any[]) => void) => {
  let unsub: (() => void) | undefined;
  let cancelled = false;
  console.log('Subscribing to board presence for boardId:', boardId);
  void ensureAuthReady().then(async () => {
    if (cancelled) return;
    await syncBoardMemberIndexViaCallable(boardId).catch(() => {});
    const q = query(
      collection(db, PRESENCE_COLLECTION),
      where('boardId', '==', boardId),
      where('isOnline', '==', true)
    );
    unsub = onSnapshot(
      q,
      (querySnapshot) => {
        const activeUsers = querySnapshot.docs.map(doc => {
          const data = doc.data();
          return {
            id: doc.id,
            ...data,
            lastSeen: convertTimestamp(data.lastSeen),
          };
        });
        console.log('Presence subscription received:', activeUsers);
        callback(activeUsers);
      },
      (err) => console.error('subscribeToBoardPresence:', err)
    );
  });
  return () => {
    cancelled = true;
    unsub?.();
  };
};

// Debug function to check all presence documents for a board
export const debugBoardPresence = async (boardId: string) => {
  try {
    const q = query(
      collection(db, PRESENCE_COLLECTION),
      where('boardId', '==', boardId)
    );
    const querySnapshot = await getDocs(q);
    
    console.log(`Debug: Found ${querySnapshot.docs.length} presence documents for board ${boardId}:`);
    querySnapshot.docs.forEach(doc => {
      const data = doc.data();
      console.log('Document ID:', doc.id, 'Data:', data);
    });
    
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      lastSeen: convertTimestamp(doc.data().lastSeen),
    }));
  } catch (error) {
    console.error('Error debugging board presence:', error);
    return [];
  }
};

// File Upload Services
export const uploadFile = async (
  file: File, 
  opportunityId: string, 
  userId: string
): Promise<FileAttachment> => {
  try {
    // Create a unique filename — userId is the first segment so storage rules can
    // enforce that only the uploading user can write to this namespace.
    const timestamp = Date.now();
    const storagePath = `opportunity-attachments/${userId}/${opportunityId}/${timestamp}_${file.name}`;

    // Create storage reference
    const storageRef = ref(storage, storagePath);
    
    // Upload file
    const snapshot = await uploadBytes(storageRef, file);
    
    // Get download URL
    const downloadURL = await getDownloadURL(snapshot.ref);
    
    // Create file attachment object
    const attachment: FileAttachment = {
      id: `${timestamp}_${file.name}`,
      name: file.name,
      url: downloadURL,
      size: file.size,
      type: file.type,
      uploadedAt: new Date(),
      uploadedBy: userId,
      storagePath,
    };
    
    return attachment;
  } catch (error) {
    console.error('Error uploading file:', error);
    throw new Error('Failed to upload file');
  }
};

export const deleteFile = async (fileUrl: string, storagePath?: string): Promise<void> => {
  try {
    let filePath: string;
    
    // If storagePath is provided, use it directly
    if (storagePath) {
      filePath = storagePath;
    } else {
      // Fall back to extracting from URL
      const url = new URL(fileUrl);
      
      // Handle different URL formats
      if (url.pathname.includes('/o/')) {
        // Standard Firebase Storage URL format
        const pathWithEncoding = url.pathname.split('/o/')[1];
        if (!pathWithEncoding) {
          throw new Error('Invalid Firebase Storage URL');
        }
        filePath = decodeURIComponent(pathWithEncoding);
      } else if (url.pathname.includes('/v0/b/')) {
        // Alternative format: /v0/b/bucket/o/path
        const parts = url.pathname.split('/');
        const bucketIndex = parts.findIndex(part => part === 'b') + 1;
        const objectIndex = parts.findIndex(part => part === 'o') + 1;
        
        if (bucketIndex < parts.length && objectIndex < parts.length) {
          const encodedPath = parts.slice(objectIndex).join('/');
          filePath = decodeURIComponent(encodedPath);
        } else {
          throw new Error('Invalid Firebase Storage URL format');
        }
      } else {
        throw new Error('Unsupported Firebase Storage URL format');
      }
    }
    
    console.log('Attempting to delete file with path:', filePath);
    
    // Create storage reference
    const storageRef = ref(storage, filePath);
    
    // Delete file
    await deleteObject(storageRef);
    console.log('File deleted successfully:', filePath);
  } catch (error) {
    console.error('Error deleting file:', error);
    throw new Error('Failed to delete file');
  }
};

// Resource Services
export const getResources = async (boardId: string): Promise<Resource[]> => {
  try {
    const q = query(
      collection(db, RESOURCES_COLLECTION),
      where("boardId", "==", boardId),
      orderBy("createdAt", "desc")
    );
    const querySnapshot = await getDocs(q);
    
    return querySnapshot.docs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        ...data,
        createdAt: convertTimestamp(data.createdAt),
        updatedAt: convertTimestamp(data.updatedAt),
      } as Resource;
    });
  } catch (error) {
    console.error("Error getting resources:", error);
    throw new Error("Failed to fetch resources");
  }
};

export const addResource = async (resource: Omit<Resource, 'id' | 'createdAt' | 'updatedAt'>, userId: string): Promise<string> => {
  try {
    const resourceData = removeUndefinedValues({
      ...resource,
      createdBy: userId,
      createdAt: convertToTimestamp(new Date()),
      updatedAt: convertToTimestamp(new Date()),
    });
    
    const docRef = await addDoc(collection(db, RESOURCES_COLLECTION), resourceData);
    return docRef.id;
  } catch (error) {
    console.error("Error adding resource:", error);
    throw new Error("Failed to add resource");
  }
};

export const updateResource = async (resourceId: string, updates: Partial<Resource>): Promise<void> => {
  try {
    const docRef = doc(db, RESOURCES_COLLECTION, resourceId);
    const updateData = removeUndefinedValues({
      ...updates,
      updatedAt: convertToTimestamp(new Date()),
    });
    
    await updateDoc(docRef, updateData);
  } catch (error) {
    console.error("Error updating resource:", error);
    throw new Error("Failed to update resource");
  }
};

export const deleteResource = async (resourceId: string): Promise<void> => {
  try {
    // First delete all allocations for this resource
    const allocationsQuery = query(
      collection(db, RESOURCE_ALLOCATIONS_COLLECTION),
      where("resourceId", "==", resourceId)
    );
    const allocationsSnapshot = await getDocs(allocationsQuery);
    const allocationDeletions = allocationsSnapshot.docs.map(doc => deleteDoc(doc.ref));
    await Promise.all(allocationDeletions);
    
    // Then delete the resource
    const docRef = doc(db, RESOURCES_COLLECTION, resourceId);
    await deleteDoc(docRef);
  } catch (error) {
    console.error("Error deleting resource:", error);
    throw new Error("Failed to delete resource");
  }
};

export const subscribeToResources = (boardId: string, callback: (resources: Resource[]) => void) => {
  let unsub: (() => void) | undefined;
  let cancelled = false;
  void ensureAuthReady().then(async () => {
    if (cancelled) return;
    console.debug('[subscribeToResources] boardId:', boardId, 'uid:', auth.currentUser?.uid);
    await syncBoardMemberIndexViaCallable(boardId).catch((e) =>
      console.warn('[subscribeToResources] sync failed:', e)
    );
    const q = query(
      collection(db, RESOURCES_COLLECTION),
      where("boardId", "==", boardId),
      orderBy("createdAt", "desc")
    );
    unsub = onSnapshot(
      q,
      (querySnapshot) => {
        const resources = querySnapshot.docs.map(doc => {
          const data = doc.data();
          return {
            id: doc.id,
            ...data,
            createdAt: convertTimestamp(data.createdAt),
            updatedAt: convertTimestamp(data.updatedAt),
          } as Resource;
        });
        callback(resources);
      },
      (err) => console.error("subscribeToResources:", err)
    );
  });
  return () => {
    cancelled = true;
    unsub?.();
  };
};

// Resource Allocation Services
export const getAllocations = async (boardId: string): Promise<ResourceAllocation[]> => {
  try {
    await ensureAuthReady();
    const q = query(
      collection(db, RESOURCE_ALLOCATIONS_COLLECTION),
      where("boardId", "==", boardId)
    );
    const querySnapshot = await runWithMemberIndexRepair(boardId, () => getDocs(q));
    
    return querySnapshot.docs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        ...data,
        createdAt: convertTimestamp(data.createdAt),
        updatedAt: convertTimestamp(data.updatedAt),
        startDate: convertOptionalTimestamp(data.startDate),
        endDate: convertOptionalTimestamp(data.endDate),
      } as ResourceAllocation;
    });
  } catch (error) {
    console.error("Error getting allocations:", error);
    throw new Error("Failed to fetch allocations");
  }
};

export const getAllocationsByResource = async (resourceId: string): Promise<ResourceAllocation[]> => {
  try {
    const q = query(
      collection(db, RESOURCE_ALLOCATIONS_COLLECTION),
      where("resourceId", "==", resourceId)
    );
    const querySnapshot = await getDocs(q);
    
    return querySnapshot.docs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        ...data,
        createdAt: convertTimestamp(data.createdAt),
        updatedAt: convertTimestamp(data.updatedAt),
        startDate: convertOptionalTimestamp(data.startDate),
        endDate: convertOptionalTimestamp(data.endDate),
      } as ResourceAllocation;
    });
  } catch (error) {
    console.error("Error getting allocations by resource:", error);
    throw new Error("Failed to fetch resource allocations");
  }
};

export const getAllocationsByProduct = async (productId: string): Promise<ResourceAllocation[]> => {
  try {
    const q = query(
      collection(db, RESOURCE_ALLOCATIONS_COLLECTION),
      where("productId", "==", productId)
    );
    const querySnapshot = await getDocs(q);
    
    return querySnapshot.docs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        ...data,
        createdAt: convertTimestamp(data.createdAt),
        updatedAt: convertTimestamp(data.updatedAt),
        startDate: convertOptionalTimestamp(data.startDate),
        endDate: convertOptionalTimestamp(data.endDate),
      } as ResourceAllocation;
    });
  } catch (error) {
    console.error("Error getting allocations by product:", error);
    throw new Error("Failed to fetch product allocations");
  }
};

export const upsertAllocation = async (allocation: Omit<ResourceAllocation, 'id' | 'createdAt' | 'updatedAt'>, userId: string): Promise<string> => {
  try {
    // Check if allocation already exists
    const existingQuery = query(
      collection(db, RESOURCE_ALLOCATIONS_COLLECTION),
      where("resourceId", "==", allocation.resourceId),
      where("productId", "==", allocation.productId)
    );
    const existingSnapshot = await getDocs(existingQuery);
    
    const toAllocationPayload = (a: Omit<ResourceAllocation, 'id' | 'createdAt' | 'updatedAt'>) => {
      const payload: Record<string, unknown> = {
        resourceId: a.resourceId,
        productId: a.productId,
        boardId: a.boardId,
        percent: a.percent,
        hoursPerMonth: a.hoursPerMonth,
        hoursPerWeek: a.hoursPerWeek,
        loeType: a.loeType,
        createdBy: a.createdBy,
        updatedAt: convertToTimestamp(new Date()),
      };
      if (a.startDate) payload.startDate = convertToTimestamp(a.startDate);
      if (a.endDate) payload.endDate = convertToTimestamp(a.endDate);
      if (a.monthlyHours && Object.keys(a.monthlyHours).length > 0) payload.monthlyHours = a.monthlyHours;
      return removeUndefinedValues(payload);
    };

    if (existingSnapshot.docs.length > 0) {
      // Update existing allocation
      const docRef = doc(db, RESOURCE_ALLOCATIONS_COLLECTION, existingSnapshot.docs[0].id);
      const updateData = toAllocationPayload({ ...allocation, createdBy: allocation.createdBy ?? '' });
      await updateDoc(docRef, updateData);
      return existingSnapshot.docs[0].id;
    } else {
      // Create new allocation
      const allocationData = removeUndefinedValues({
        ...toAllocationPayload(allocation),
        createdBy: userId,
        createdAt: convertToTimestamp(new Date()),
      });
      
      const docRef = await addDoc(collection(db, RESOURCE_ALLOCATIONS_COLLECTION), allocationData);
      return docRef.id;
    }
  } catch (error) {
    console.error("Error upserting allocation:", error);
    throw new Error("Failed to save allocation");
  }
};

export const deleteAllocation = async (allocationId: string): Promise<void> => {
  try {
    const docRef = doc(db, RESOURCE_ALLOCATIONS_COLLECTION, allocationId);
    await deleteDoc(docRef);
  } catch (error) {
    console.error("Error deleting allocation:", error);
    throw new Error("Failed to delete allocation");
  }
};

/** Re-point an allocation at a different resource (e.g. replace a placeholder with a real person). */
export const updateAllocationResourceId = async (allocationId: string, newResourceId: string): Promise<void> => {
  try {
    const docRef = doc(db, RESOURCE_ALLOCATIONS_COLLECTION, allocationId);
    await updateDoc(docRef, {
      resourceId: newResourceId,
      updatedAt: convertToTimestamp(new Date()),
    });
  } catch (error) {
    console.error("Error updating allocation resource:", error);
    throw new Error("Failed to update allocation resource");
  }
};

// Standup Entry Services
export interface StandupEntry {
  id?: string;
  boardId: string;
  userId: string;
  userName: string;
  userEmail: string;
  userPhotoURL?: string;
  focusedOn: string;
  nextUp: string;
  funFact: string;
  date: string;
  updatedAt: Date;
}

export const getStandupEntries = async (boardId: string, date: string): Promise<StandupEntry[]> => {
  try {
    const q = query(
      collection(db, STANDUP_ENTRIES_COLLECTION),
      where("boardId", "==", boardId),
      where("date", "==", date)
    );
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      updatedAt: convertTimestamp(doc.data().updatedAt),
    })) as StandupEntry[];
  } catch (error) {
    console.error("Error fetching standup entries:", error);
    throw new Error("Failed to fetch standup entries");
  }
};

export const getStandupEntry = async (boardId: string, userId: string, date: string): Promise<StandupEntry | null> => {
  try {
    const q = query(
      collection(db, STANDUP_ENTRIES_COLLECTION),
      where("boardId", "==", boardId),
      where("userId", "==", userId),
      where("date", "==", date)
    );
    const querySnapshot = await getDocs(q);
    if (querySnapshot.empty) {
      return null;
    }
    const docData = querySnapshot.docs[0].data();
    return {
      id: querySnapshot.docs[0].id,
      ...docData,
      updatedAt: convertTimestamp(docData.updatedAt),
    } as StandupEntry;
  } catch (error) {
    console.error("Error fetching standup entry:", error);
    throw new Error("Failed to fetch standup entry");
  }
};

export const upsertStandupEntry = async (entry: Omit<StandupEntry, 'id' | 'updatedAt'>): Promise<string> => {
  try {
    // Check if entry already exists
    const existingQuery = query(
      collection(db, STANDUP_ENTRIES_COLLECTION),
      where("boardId", "==", entry.boardId),
      where("userId", "==", entry.userId),
      where("date", "==", entry.date)
    );
    const existingSnapshot = await getDocs(existingQuery);
    
    const entryData = removeUndefinedValues({
      ...entry,
      updatedAt: convertToTimestamp(new Date()),
    });

    if (existingSnapshot.docs.length > 0) {
      // Update existing entry
      const docRef = doc(db, STANDUP_ENTRIES_COLLECTION, existingSnapshot.docs[0].id);
      await updateDoc(docRef, entryData);
      return existingSnapshot.docs[0].id;
    } else {
      // Create new entry
      const docRef = await addDoc(collection(db, STANDUP_ENTRIES_COLLECTION), entryData);
      return docRef.id;
    }
  } catch (error) {
    console.error("Error upserting standup entry:", error);
    throw new Error("Failed to save standup entry");
  }
};

export const subscribeToAllocations = (
  boardId: string,
  callback: (allocations: ResourceAllocation[]) => void
) => {
  let unsub: (() => void) | undefined;
  let cancelled = false;
  void ensureAuthReady().then(async () => {
    if (cancelled) return;
    console.debug('[subscribeToAllocations] boardId:', boardId, 'uid:', auth.currentUser?.uid);
    await syncBoardMemberIndexViaCallable(boardId).catch((e) =>
      console.warn('[subscribeToAllocations] sync failed:', e)
    );
    const q = query(
      collection(db, RESOURCE_ALLOCATIONS_COLLECTION),
      where("boardId", "==", boardId)
    );
    unsub = onSnapshot(
      q,
      (querySnapshot) => {
        const allocations = querySnapshot.docs.map(doc => {
          const data = doc.data();
          return {
            id: doc.id,
            ...data,
            createdAt: convertTimestamp(data.createdAt),
            updatedAt: convertTimestamp(data.updatedAt),
            startDate: convertOptionalTimestamp(data.startDate),
            endDate: convertOptionalTimestamp(data.endDate),
          } as ResourceAllocation;
        });
        callback(allocations);
      },
      (err) => console.error("subscribeToAllocations:", err)
    );
  });
  return () => {
    cancelled = true;
    unsub?.();
  };
};

// Resource roles (bill / cost rates per job title, per board)
export const subscribeToResourceRoles = (
  boardId: string,
  callback: (roles: ResourceRole[]) => void,
  onError?: (err: Error) => void
) => {
  let unsub: (() => void) | undefined;
  let cancelled = false;
  void ensureAuthReady().then(async () => {
    if (cancelled) return;
    console.debug('[subscribeToResourceRoles] boardId:', boardId, 'uid:', auth.currentUser?.uid);
    await syncBoardMemberIndexViaCallable(boardId).catch((e) =>
      console.warn('[subscribeToResourceRoles] sync failed:', e)
    );
    const q = query(collection(db, RESOURCE_ROLES_COLLECTION), where("boardId", "==", boardId));
    unsub = onSnapshot(
      q,
      (querySnapshot) => {
        const roles = querySnapshot.docs
          .map(docSnap => {
            const data = docSnap.data();
            return {
              id: docSnap.id,
              ...data,
              createdAt: convertTimestamp(data.createdAt),
              updatedAt: convertTimestamp(data.updatedAt),
            } as ResourceRole;
          })
          .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: "base" }));
        callback(roles);
      },
      (err) => {
        console.error("subscribeToResourceRoles:", err);
        onError?.(err as Error);
      }
    );
  });
  return () => {
    cancelled = true;
    unsub?.();
  };
};

export const addResourceRole = async (
  payload: Omit<ResourceRole, "id" | "createdAt" | "updatedAt" | "createdBy">,
  userId: string
): Promise<string> => {
  const docRef = await addDoc(collection(db, RESOURCE_ROLES_COLLECTION), removeUndefinedValues({
    ...payload,
    createdBy: userId,
    createdAt: convertToTimestamp(new Date()),
    updatedAt: convertToTimestamp(new Date()),
  }));
  return docRef.id;
};

export const updateResourceRole = async (
  roleId: string,
  updates: Partial<Pick<ResourceRole, "name" | "billRatePerHour" | "costRatePerHour">>
): Promise<void> => {
  await updateDoc(doc(db, RESOURCE_ROLES_COLLECTION, roleId), removeUndefinedValues({
    ...updates,
    updatedAt: convertToTimestamp(new Date()),
  }));
};

/** Rename a role and update all resources on the board that use the old role name. */
export const renameResourceRoleAndResources = async (
  roleId: string,
  boardId: string,
  oldName: string,
  newName: string
): Promise<void> => {
  const trimmed = newName.trim();
  if (!trimmed) {
    throw new Error("Role name is required");
  }
  if (trimmed === oldName) {
    return;
  }
  const batch = writeBatch(db);
  batch.update(doc(db, RESOURCE_ROLES_COLLECTION, roleId), removeUndefinedValues({
    name: trimmed,
    updatedAt: convertToTimestamp(new Date()),
  }));
  const resQ = query(collection(db, RESOURCES_COLLECTION), where("boardId", "==", boardId));
  const resSnap = await getDocs(resQ);
  const now = convertToTimestamp(new Date());
  resSnap.docs.forEach(d => {
    const data = d.data();
    if (data.role === oldName) {
      batch.update(d.ref, { role: trimmed, updatedAt: now });
    }
  });
  await batch.commit();
};

export const deleteResourceRole = async (roleId: string): Promise<void> => {
  await deleteDoc(doc(db, RESOURCE_ROLES_COLLECTION, roleId));
};

/** Create Firestore rows for any default role names not yet present on this board (safe to call repeatedly). */
export const ensureMissingDefaultResourceRoles = async (boardId: string, userId: string): Promise<void> => {
  await ensureAuthReady();
  const snap = await getDocs(
    query(collection(db, RESOURCE_ROLES_COLLECTION), where("boardId", "==", boardId))
  );
  const existingNames = new Set(
    snap.docs.map(d => String((d.data() as { name?: string }).name ?? "").trim()).filter(Boolean)
  );
  const batch = writeBatch(db);
  const now = convertToTimestamp(new Date());
  let count = 0;
  DEFAULT_RESOURCE_ROLE_NAMES.forEach(name => {
    if (existingNames.has(name)) return;
    const ref = doc(collection(db, RESOURCE_ROLES_COLLECTION));
    batch.set(ref, removeUndefinedValues({
      boardId,
      name,
      billRatePerHour: 0,
      costRatePerHour: 0,
      createdBy: userId,
      createdAt: now,
      updatedAt: now,
    }));
    count++;
  });
  if (count > 0) await batch.commit();
};

/** @deprecated Prefer ensureMissingDefaultResourceRoles — seeds only when collection was empty. */
export const seedDefaultResourceRoles = async (boardId: string, userId: string): Promise<void> => {
  await ensureMissingDefaultResourceRoles(boardId, userId);
};

/** Join via share link (server validates link; updates board + link atomically). */
export async function joinBoardViaShareLink(shareLinkId: string): Promise<void> {
  const fn = httpsCallable(getFunctions(), 'joinBoardWithShareLink');
  await fn({ shareLinkId });
}

/** Accept board invitation (server verifies recipient email). */
export async function acceptInvitationViaCallable(invitationId: string): Promise<void> {
  const fn = httpsCallable(getFunctions(), 'acceptBoardInvitation');
  await fn({ invitationId });
}
